<template>
  <v-container id="documents_root" fluid>
    <router-view name="dialog" @uploaded="searchSignatureChains"></router-view>

    <!-- CONTENT -->
    <v-row class="mb-6 px-6" align="center" justify="center">
      <v-col>
        <span class="text-h5">
          {{ $t('documents.title.list') }}
        </span>
      </v-col>

      <v-col class="d-flex flex-row align-self-end" align="sretch">
        <v-tooltip bottom :disabled="currentUser.roles.includes(userRole.CREATOR)">
          <template #activator="{ on, attrs }">
            <v-btn
              id="upload_document" v-bind="attrs" :disabled="!currentUser.roles.includes(userRole.CREATOR)"
              class="align-self-end mr-8" color="primary" align="right" v-on="on" @click="handleUploadClick"
            >
              <v-img class="mr-2" src="@/assets/icons/upload-doc.png"></v-img>
              <v-spacer></v-spacer>
              <span class="text-capitalize">{{ $t('documents.content.uploadDocument') }}</span>
            </v-btn>
          </template>
          <span>{{ $t('documents.content.noRoleTooltip') }}</span>
        </v-tooltip>

        <v-text-field
          id="search_field" :label="$t('dataTable.searchMulti')" append-icon="mdi-magnify" single-line
          hide-details clearable @input="filterSignatureChainOrigDocFileName"
        ></v-text-field>
      </v-col>
    </v-row>

    <v-row class="mb-6 px-6 filterbar" align="center" justify="start">
      <!-- Change view -->
      <v-btn
        id="multiple_selection_switch" class="px-2 mr-2 " :outlined="bulkSignMode"
        :color="bulkSignMode ? 'info' : 'filterbutton'" @click="switchBulkSign"
      >
        <v-icon color="info" class="mx-1">mdi-checkbox-multiple-blank-outline</v-icon>
        {{ $t('documents.content.multipleSelectButton') }}
      </v-btn>
      <v-btn v-if="selected.length > 1" id="multi_sign" class="primary mr-2 elevation-8" @click="multiSign">
        <v-icon class="ml-0 mr-1">mdi-draw-pen</v-icon>
        {{ $t('documents.content.multipleSignButton') }}
      </v-btn>
      <v-btn
        id="thumbnailview_toggle_btn" color="filterbutton pl-2 pr-0 mr-2" elevation="1"
        @click="toggleThumbnailView()"
      >
        <span class="subtitle-2 pr-2">
          {{ $t('documents.content.changeViewButton') }}
        </span>
        <v-icon class="mx-0" small>mdi-view-agenda-outline</v-icon>
        <v-icon class="mx-0 pr-1" small>mdi-unfold-more-horizontal</v-icon>
      </v-btn>

      <!-- signatureChainStatusFilters -->
      <span class="subtitle-1 mr-4">{{ $t('documents.content.filters') }}</span>
      <v-chip-group
        v-model="statusFilter" class="filterButtonChip" :color="getFilterChipColor()" mandatory
        @change="filterSignatureChainStatus"
      >
        <div v-for="filter in signatureChainStatusFilters" :key="filter.value" class="d-inline-block">
          <v-badge
            :id="filter.id" color="red" overlap offset-x="19" dot class="noti-badge"
            :value="filter.notifications"
          >
            <v-chip
              class="elevation-1" color="white" label :value="filter.value" filter
              filter-icon="mdi-close-circle-outline"
            >
              {{ filter.text }}
            </v-chip>
          </v-badge>
        </div>
      </v-chip-group>
      <template v-if="isInThumbnailView">
        <span class="mr-4">{{ $t('signatureChains.sortBy') }}:</span>
        <v-menu bottom offset-y>
          <template #activator="{ on }">
            <v-btn class="category-btn elevation-1" color="white" v-on="on">
              <span class="menu-link">{{ currentSorting.text }}</span>
            </v-btn>
          </template>
          <v-list>
            <v-list-item
              v-for="sortOption in sortableHeaders" :key="sortOption.value" link
              @click="sortingChanged(sortOption)"
            >
              <v-list-item-content>
                <v-list-item-title class="menu-link">{{ sortOption.text }}</v-list-item-title>
              </v-list-item-content>
            </v-list-item>
          </v-list>
        </v-menu>
        <v-btn class="sortdirection-btn elevation-1" color="white" min-width="40" @click="toggleSort">
          <v-icon>{{ currentSorting.isDesc ? 'mdi-chevron-down' : 'mdi-chevron-up' }}</v-icon>
        </v-btn>
      </template>
    </v-row>

    <!-- DATATABLE VIEW -->
    <v-card v-show="!isInThumbnailView" id="datatable_view" class="mt-4">
      <v-data-table
        id="documents_table" v-model="selected" must-sort :headers="headers" :items="signatureChainList"
        :options.sync="signatureChainPagination" :server-items-length="totalItems" :loading="dataTableLoading"
        :loading-text="$t('dataTable.content.loadingPleaseWait')"
        :no-data-text="$t('dataTable.content.noDataAvailable')"
        :no-results-text="$t('dataTable.content.noMatchingRecordsFound')" :footer-props="{
          'items-per-page-text': $t('dataTable.footer.itemsPerPageText'),
          'page-text': $t('dataTable.footer.pageText'),
          'items-per-page-options': [10, 25, 50],
        }" :update:options="signatureChainList" calculate-widths :show-select="bulkSignMode && statusFilter === 'TODO'"
        :single-select="false" checkbox-color="info"
      >
        <template #[`item.data-table-select`]="{ item }">
          <v-checkbox
            v-if="bulkSignMode &&
              statusFilter === 'TODO' &&
              !!item.origDocument &&
              !item.signatures.some(x => x.clerkMode || x.approver)" id="chain_checkbox" v-model="selected"
            :value="item"
          ></v-checkbox>
        </template>
        <template #[`item.origDocument.fileName`]="{ item }">
          <a v-if="item.origDocument" class="black--text text--primary" @click="openInNewTab(item, $event)">
            <long-text :text="item.origDocument.fileName" :max-char="60" />
          </a>
          <span v-else> <long-text :text="item.batch.name" :max-char="60" /></span>
        </template>
        <template #[`item.status`]="{ item }">
          <v-chip id="status-badge" :color="getChipStyle(item)" label>
            {{ getChipText(item) }}
          </v-chip>
        </template>
        <template #[`item.createdAt`]="{ item }">
          <div class="font-italic caption">
            {{ convertDate(item.createdAt) }}
          </div>
        </template>
        <template #[`item.updatedAt`]="{ item }">
          <div v-if="!!item.updatedAt" class="font-italic caption">
            {{ convertDate(item.updatedAt) }}
          </div>
        </template>

        <template #[`item.actions`]="{ item }">
          <!-- VIEWER -->
          <template v-if="isViewOnly(item)">
            <v-tooltip bottom>
              <template #activator="{ on, attrs }">
                <div class="d-inline-block" v-bind="attrs" v-on="on">
                  <v-btn class="viewer-btn rounded-lg pa-1 px-2 mr-2  " disabled outlined>
                    <v-icon class=" text--darken-6">mdi-file-eye-outline</v-icon>
                    <span class=" text--darken-2">
                      {{ $t('signatureChains.states.viewer') }}
                    </span>
                  </v-btn>
                </div>
              </template>
              <span>{{ $t('signatureChains.states.viewerExplainer') }}</span>
            </v-tooltip>
          </template>

          <!-- IN PROGRESS-->
          <template v-if="isInProgress(item.signatures)">
            <v-tooltip bottom>
              <template #activator="{ on, attrs }">
                <div class="d-inline-block" v-bind="attrs" v-on="on">
                  <v-btn class="inprogress-btn rounded-lg pa-1 px-2 mr-2  " disabled outlined>
                    <v-icon class=" text--darken-6">mdi-progress-pencil</v-icon>
                    <span class=" text--darken-2">
                      {{ $t('signatureChains.states.inProgress') }}
                    </span>
                  </v-btn>
                </div>
              </template>
              <span>{{ $t('signatureChains.states.inProgressExplainer') }}</span>
            </v-tooltip>
          </template>

          <template v-else>
            <!-- SIGN -->
            <v-btn
              v-if="item.origDocument && canUserSign(item) && !isApproveMode(item)" id="sign_button"
              class="sign-btn pa-1 px-5 mr-2 primary" text @click="navToSign(item)"
            >
              <v-icon>mdi-draw-pen</v-icon>
              {{ $t('dataTable.content.action.sign') }}
            </v-btn>
            <v-btn
              v-else-if="!item.origDocument && canUserSign(item)" id="sign_button"
              class="sign-btn pa-1 px-5 mr-2 primary" text @click="asicSign(item)"
            >
              <v-icon>mdi-draw-pen</v-icon>
              {{ $t('dataTable.content.action.sign') }}
            </v-btn>
            <!-- APPROVE -->
            <v-btn
              v-if="!!item.origDocument && canUserSign(item)&& isApproveMode(item)"
              id="approve_button"
              class="sign-btn pa-1 px-4 mr-2 primary" @click="navToApprove(item)"
            >
              <v-icon>mdi-check-circle-outline</v-icon>
              {{ $t('dataTable.content.action.approve') }}
            </v-btn>
            <!-- WITHDRAW -->
            <v-btn
              v-if="canWithdraw(item)" outlined color="error" class="withdraw-btn pa-1 pr-2 mr-2"
              @click="withdrawSignatureChain(item.id, $event, searchSignatureChainsWatcher)"
            >
              <v-icon color="error">mdi-file-undo-outline</v-icon>
              {{ $t('dataTable.content.action.withdraw') }}
            </v-btn>

            <!-- DECLINE -->
            <v-btn
              v-if="canUserSign(item) && item.createdBy?.id !== currentUser.id" outlined color="error"
              class="decline-btn pa-1 pr-2 mr-2" @click="showDeclineModal(item, $event)"
            >
              <v-icon>mdi-close-circle-outline</v-icon>
              {{ $t('dataTable.content.action.decline') }}
            </v-btn>
          </template>

          <!-- PARTICIPANTS -->
          <v-btn id="participant_button" class="participants-btn pa-1  " text @click="showParticipants(item)">
            <v-icon>mdi-account-multiple</v-icon>
            {{ $t('signatureChains.participants.title') }}
          </v-btn>

          <!-- DOWNLOAD -->
          <v-btn id="download_button" class="download-btn pa-1  " text @click="downloadDocument(item, $event)">
            <v-icon>mdi-tray-arrow-down</v-icon>
            {{ $t('dataTable.content.action.download') }}
          </v-btn>

          <!-- DELETE -->
          <v-tooltip bottom :disabled="canUserDeleteChain(item)">
            <template #activator="{ on }">
              <div class="d-inline-block" v-on="on">
                <v-btn
                  class="delete-btn pa-1  text--darken-5" :disabled="!canUserDeleteChain(item)" text
                  @click="deleteSignatureChain(item, $event, searchSignatureChainsWatcher)"
                >
                  <v-icon>mdi-delete</v-icon>
                  {{ $t('dataTable.content.action.delete') }}
                </v-btn>
              </div>
            </template>
            <span>{{ $t('dataTable.content.action.cantDeleteExplainer') }}</span>
          </v-tooltip>

          <!-- INFO MENU -->
          <v-btn class="info-btn" icon @click="showInfoMenu(item, $event['clientX'], $event['clientY'])">
            <v-icon>mdi-information-outline</v-icon>
          </v-btn>
        </template>
      </v-data-table>
    </v-card>

    <!-- Thumbnail view -->
    <template v-if="isInThumbnailView">
      <v-container id="thumbnail_view">
        <v-row>
          <document-row
            :multiple-selection="bulkSignMode" :dashboard-mode="false" :items="signatureChainList"
            :no-documents="$t('dataTable.content.noDataAvailable')" :status="statusFilter"
            @changed="searchSignatureChains()" @selectionChanged="handleSelectionChanged($event)"
          ></document-row>
        </v-row>

        <v-row justify="center" align="end">
          <v-btn
            v-if="totalItems > signatureChainList.length" id="show_more" class="mx-2" large
            color="black--text grey lighten-2" @click="loadMore"
          >
            <div>{{ $t('main.showMore') }}</div>
            <v-icon>
              mdi-chevron-double-down
            </v-icon>
          </v-btn>
        </v-row>
      </v-container>
    </template>

    <!-- MENU -->
    <v-menu v-model="menuVisible" :position-x="menuX" :position-y="menuY" absolute>
      <v-card>
        <v-card-text>
          <v-container class="black--text body-2">
            <v-row>{{ $t('dataTable.header.documents.id') }} : {{ currentDoc.id }}</v-row>
            <v-row>{{ $t('dataTable.header.documents.createdAt') }} : {{ convertDate(currentDoc.createdAt) }}</v-row>
            <!-- <v-row v-if="currentDoc.origDocument">{{ $t('dataTable.header.documents.fileSize') }} : {{ currentDoc.origDocument.fileSize }} {{ $t('dataTable.header.documents.fileSizeInByte') }}</v-row> -->
          </v-container>
        </v-card-text>
      </v-card>
    </v-menu>

    <!-- PARTICIPANTS DIALOG -->
    <v-dialog v-model="showParticipantsDialog" max-width="800" class="rounded-xl">
      <signature-chain-participants
        v-if="showParticipantsDialog" :signature-chain="chosenSignatureChain"
        @close="showParticipantsDialog = false"
      />
    </v-dialog>

    <decline-reason-modal
      v-if="declineReasonVisible" :visible="declineReasonVisible"
      :signature-id="selectedUserSignatureId" :current-signature-id="selectedCurrentSignatureId"
      @declined="declineModalClosed" @closed="declineReasonVisible = false"
    >
    </decline-reason-modal>

    <download-mode-menu
      :pos-x="downloadMenuX" :pos-y="downloadMenuY" :visible="downloadMenuVisible"
      :new-tab="downloadModalNewTab"
    >
    </download-mode-menu>
    <v-row justify="center">
      <v-dialog v-model="centralKeyNotActiveFileDropWarning" persistent width="auto" max-width="700">
        <v-card id="no_credential_warning" class="py-6 px-3">
          <v-card-title class="text-h6 red--text">
            {{ $t('signatureProfiles.error.centralKeyNotEnabled.title') }}
          </v-card-title>
          <v-card-text>
            <div>
              {{ $t('signatureProfiles.error.centralKeyNotEnabled.description') }}
            </div>
            <v-btn
              id="no_credential_warning_ok" style="text-align: center;" color="primary" class="mt-10 mb-0"
              @click="centralKeyNotActiveFileDropWarning = false"
            >
              {{ $t('signatureProfiles.error.centralKeyNotEnabled.button') }}
            </v-btn>
          </v-card-text>
        </v-card>
      </v-dialog>
    </v-row>
  </v-container>
</template>

<script>
import DocumentRow from '@/components/DocumentRow'
import LongText from '@/components/LongText'
import eventTypes from '@/eventTypes.js'
import documents from '@/services/api/documents'
import rssp from '@/services/api/rsspUtil.js'
import seal from '@/services/api/seal.js'
import FileSaver from 'file-saver'
import debounce from 'lodash.debounce'
import eventBus, { showConfirmationMenu, showToastError } from '../../../event-bus.js'
import signatureChains from '../../../services/api/signatureChains'
import DeclineReasonModal from '../DeclineReasonModal'
import DownloadModeMenu from './DownloadModeMenu'
import SignatureChainListStatus from './SignatureChainListStatus'
import SignatureChainParticipants from './SignatureChainParticipants'
import SignatureChainStatus from './SignatureChainStatus.js'
import SignatureChainType from './SignatureChainType'
import documentDownloadMode from './documentDownloadMode.js'
import userRole from './userRole'

export default {
  name: 'SignatureChains',

  components: {
    SignatureChainParticipants,
    LongText,
    DocumentRow,
    DeclineReasonModal,
    DownloadModeMenu
  },

  beforeRouteLeave(to, from, next) {
    eventBus.$off(eventTypes.SIGNATURE_CHAIN_CREATED, this.onChainCreated)
    eventBus.$off(eventTypes.SIGNATURE_CHAIN_UPDATED, this.onChainUpdated)
    eventBus.$off(eventTypes.SIGNATURE_CHAIN_DELETED, this.onChainDeleted)
    eventBus.$off(eventTypes.SIGNATURE_CHAIN_WITHDRAWN, this.onChainUpdated)

    next()
  },

  data() {
    return {
      userRole,
      filters: {
        signatureChainStatus: null,
        signatureChainOrigDocFileName: null,
        signatureChainCreatedBy: null
      },
      isInThumbnailView: true,
      signatureChainPagination: {
        sortBy: ['updatedAt'],
        sortDesc: [true]
      },
      signatureChainList: [],
      totalItems: 0,
      dataTableLoading: false,
      signatureChainStatusFilters: [
        {
          id: 'status_filter_all',
          value: SignatureChainListStatus.ALL,
          text: this.$t('signatureChains.states.all'),
          notifications: false
        },
        {
          id: 'status_filter_todo',
          value: SignatureChainListStatus.TODO,
          text: this.$t('signatureChains.states.todo'),
          notifications: false
        },
        {
          id: 'status_filter_pending',
          value: SignatureChainListStatus.PENDING,
          text: this.$t('signatureChains.states.pending'),
          notifications: false
        },
        {
          id: 'status_filter_signed',
          value: SignatureChainListStatus.SIGNED,
          text: this.$t('signatureChains.states.completed'),
          notifications: false
        },
        {
          id: 'status_filter_failed',
          value: SignatureChainListStatus.FAILED.FAILED,
          text: this.$t('signatureChains.states.failedTypes.failed'),
          notifications: false
        }
      ],
      headers: [
        {
          text: this.$t('dataTable.header.signatureChains.fileName'),
          align: 'left',
          value: 'origDocument.fileName',
          width: '15%',
          sortable: true
        },
        {
          text: this.$t('dataTable.header.signatureChains.status'),
          value: 'status',
          sortable: false
        },
        {
          text: this.$t('dataTable.header.signatureChains.createdAt'),
          value: 'createdAt',
          sortable: true,
          align: 'center'
        },
        {
          text: this.$t('dataTable.header.signatureChains.updatedAt'),
          value: 'updatedAt',
          sortable: true,
          align: 'center'
        },
        {
          text: this.$t('dataTable.header.actions'),
          align: 'right',
          value: 'actions',
          sortable: false
        }
      ],
      sortableHeaders: [
        {
          text: this.$t('dataTable.header.signatureChains.fileName'),
          align: 'left',
          value: 'origDocument.fileName',
          width: '15%',
          sortable: true
        },
        {
          text: this.$t('dataTable.header.signatureChains.updatedAt'),
          value: 'updatedAt',
          sortable: true,
          align: 'center'
        },
        {
          text: this.$t('dataTable.header.signatureChains.createdAt'),
          value: 'createdAt',
          sortable: true,
          align: 'center'
        }
      ],
      // dialog
      showDialog: false,
      // dialog - participants
      showParticipantsDialog: false,
      chosenSignatureChain: null,

      // info menu
      menuVisible: false,
      menuX: 0,
      menuY: 0,
      currentDoc: {
        createdAt: '',
        type: '',
        origDocument: {
          id: '4',
          fileSize: 146583,
          createdAt: ''

        }
      },
      // download mode menu
      downloadMenuVisible: false,
      downloadMenuX: 0,
      downloadMenuY: 0,
      downloadModalNewTab: false,

      // filter
      statusFilter: this.$route.params.filter || 'ALL',

      // helper for thumbnail view sorting button
      currentSorting: {
        value: '',
        text: '',
        isDesc: false
      },
      pageThumbnail: 1,
      // decline modal:
      declineReasonVisible: false,
      selectedUserSignatureId: '',
      selectedCurrentSignatureId: '',
      sealStatus: {
        enabled: false,
        secureMode: false,
        activated: false
      },
      activatedCredentials: [],
      centralKeyNotActiveFileDropWarning: false,
      selected: [],
      bulkSignMode: false
    }
  },

  computed: {
    currentUser() {
      return this.$store.state.currentUser
    },
    isExternalListing() {
      return this.$route.query.q
    },
    canUpload() {
      return !this.sealStatus.enabled || this.sealStatus.activated || (this.activatedCredentials.length > 0)
    }
  },

  watch: {
    filters() {
      this.dataTableLoading = true
      this.searchSignatureChainsWatcher()
    },

    signatureChainPagination: {
      handler(val) {
        this.searchSignatureChains(this.statusFilter)
        this.currentSorting.value = val.sortBy[0]

        switch (this.currentSorting.value) {
          case 'origDocument.fileName':
            this.currentSorting.text = this.$t('dataTable.header.signatureChains.fileName')
            break
          case 'createdAt':
            this.currentSorting.text = this.$t('dataTable.header.signatureChains.createdAt')
            break
          case 'updatedAt':
            this.currentSorting.text = this.$t('dataTable.header.signatureChains.updatedAt')
            break
          default:
            break
        }

        this.currentSorting.isDesc = val.sortDesc[0]
        this.setPaginationCookies()
      }

    },

    statusFilter() {
      switch (this.statusFilter) {
        case SignatureChainListStatus.TODO: this.noDocText = this.$t('dashboard.documentRows.todo.noDoc')
          break
        case SignatureChainListStatus.PENDING: this.noDocText = this.$t('dashboard.documentRows.pending.noDoc')
          this.bulkSignMode = false
          break
        case SignatureChainListStatus.SIGNED: this.noDocText = this.$t('dashboard.documentRows.completed.noDoc')
          this.bulkSignMode = false
          break
        default: this.$t('dataTable.content.noDataAvailable')
          this.bulkSignMode = false
          break
      }

      this.setPaginationCookies()
    },
    signatureChainList() {
      this.selected = []
    }
  },

  async mounted() {
    const data = this.$cookies.get('doc-pagination')
    if (data) {
      this.isInThumbnailView = data.thumbnailView
      this.currentSorting.isDesc = true
    }

    if (this.isExternalListing) {
      this.filters.signatureChainOrigDocFileName = this.$route.query.q
    }

    this.sealStatus = await seal.getSealStatus()
    await this.listMyActivatedCredentials()

    eventBus.$on(eventTypes.SIGNATURE_CHAIN_CREATED, this.onChainCreated)
    eventBus.$on(eventTypes.SIGNATURE_CHAIN_UPDATED, this.onChainUpdated)
    eventBus.$on(eventTypes.SIGNATURE_CHAIN_DELETED, this.onChainDeleted)
    eventBus.$on(eventTypes.SIGNATURE_CHAIN_WITHDRAWN, this.onChainUpdated)
  },

  methods: {
    async searchSignatureChains() {
      // filter if selected

      this.hideNotifications()
      this.dataTableLoading = true

      let realSortField = this.signatureChainPagination.sortBy[0]
      if (realSortField === 'origDocument.fileName') {
        realSortField = 'fileName'
      }

      const q = {
        status: this.statusFilter == null ? null : this.statusFilter,
        q: this.filters.signatureChainOrigDocFileName
      }

      const pageRequest = {
        'page-num': this.signatureChainPagination.page,
        'page-size': (this.signatureChainPagination.itemsPerPage || 10), // this.signatureChainPagination.itemsPerPage || (!this.isInThumbnailView ? 50 : 10),
        'sort-field': realSortField,
        'sort-order': this.signatureChainPagination.sortDesc[0] ? 'desc' : 'asc'
      }

      try {
        const page = await signatureChains.listMySignatureChains(q, pageRequest)
        this.signatureChainList = JSON.parse(JSON.stringify(page.content))
        this.totalItems = page.total

        if (!this.isExternalListing && !this.$route.fullPath.includes('upload')) {
          this.routerReplace({ name: 'SignatureChains', params: { filter: this.statusFilter } })
        }

        if (this.showParticipantsDialog) {
          const freshChain = this.signatureChainList.find(x => x.id === this.chosenSignatureChain.id)
          this.showParticipants(freshChain)
        }
      } catch (error) {
        showToastError(this.getErrorMessage(error))
      } finally {
        this.dataTableLoading = false
      }
    },

    searchSignatureChainsWatcher: debounce(function() {
      this.searchSignatureChains()
    }, 500),

    filterSignatureChainStatus(val) {
      this.filters = Object.assign({}, this.filters, { signatureChainStatus: val })
    },

    filterSignatureChainOrigDocFileName(val) {
      this.filters = Object.assign({}, this.filters, { signatureChainOrigDocFileName: val })
    },

    async showDeclineModal(chain, e) {
      const conf = await showConfirmationMenu({
        title: this.$t('dialog.confirmation.title'),
        posX: e.clientX,
        posY: e.clientY
      })
      if (!conf) {
        return
      }

      this.selectedCurrentSignatureId = this.getCurrentSignatureId(chain)
      this.selectedUserSignatureId = this.getUserSignatureId(chain)
      this.declineReasonVisible = true
    },

    canUserSign(item) {
      const usersSignatures = item?.signatures?.filter(x => !!x.user && x.user.id === this.currentUser.id)
      if (
        usersSignatures?.length > 0 &&
    usersSignatures.some(signature => signature.status === 'CREATED') &&
    item?.status === SignatureChainStatus.CREATED
      ) {
        const currentUserIndex = item?.signatures?.findIndex(
          signature => usersSignatures.find(x => x.id === signature.id) && signature.status === 'CREATED'
        )
        const previousUserIndex = (currentUserIndex - 1)

        if (currentUserIndex === -1) {
          return false
        }
        if (item.signatures[currentUserIndex].status !== 'CREATED') {
          return false
        }
        if (
          item.type === SignatureChainType.UNORDERED ||
      currentUserIndex === 0 ||
      item.signatures[previousUserIndex].status !== 'CREATED'
        ) {
          return true
        }
      }

      return false
    },

    isApproveMode(item) {
      const isOrdered = item.type === SignatureChainType.ORDERED

      if (!isOrdered) {
        return item.signatures.some(signature => {
          return (
            signature.user?.id === this.currentUser.id &&
            signature.status === SignatureChainStatus.CREATED &&
            !!signature.approver
          )
        })
      } else {
        const firstSignature = item.signatures.find(signature => signature.status === 'CREATED' && signature.user.id === this.currentUser.id)
        return firstSignature.approver
      }
    },

    getUserSignatureId(item) {
      const userSignature = item.signatures.find(signature => (!!signature.user && signature.user.id === this.currentUser.id && signature.status === SignatureChainStatus.CREATED))
      return userSignature.id
    },

    getUserSignatureIdForApprover(item) {
      const userSignature = item.signatures.find(signature => (!!signature.user && signature.user.id === this.currentUser.id && signature.status === SignatureChainStatus.CREATED && signature.approver === true))
      return userSignature.id
    },
    getUserSignatureIdForClerk(item) {
      const userSignature = item.signatures.find(signature => (!!signature.user && signature.user.id === this.currentUser.id && signature.status === SignatureChainStatus.CREATED && signature.clerkMode === true))
      return userSignature.id
    },

    getCurrentSignatureId(item) {
      if (item.type === SignatureChainType.UNORDERED && !!item.currentSignatureId) {
        return item.currentSignatureId
      } else return ''
    },

    moreThanOneParticipants(chain) {
      if (chain.signatures.length > 1) return true
      return false
    },

    showParticipants(chain) {
      this.showDialog = true
      this.showParticipantsDialog = true
      this.chosenSignatureChain = chain
    },

    showInfoMenu(item, x, y) {
      this.currentDoc = item
      this.menuX = x
      this.menuY = y
      this.menuVisible = true
    },

    getChipStyle(chain) {
      if (chain.status === SignatureChainStatus.DONE) {
        return 'signed'
      } else if (this.canUserSign(chain)) {
        return 'todo'
      } else if (chain.status === SignatureChainStatus.FAILED) {
        return 'failed error lighten-3 error--text text--darken-2'
      } else {
        return 'pending'
      }
    },

    getChipText(chain) {
      if (chain.status === SignatureChainStatus.DONE) {
        return this.$t('signatureChains.states.completed')
      } else if (this.canUserSign(chain)) {
        return this.$t('signatureChains.states.todo')
      } else if (chain.status === SignatureChainStatus.FAILED) {
        return this.$t('signatureChains.states.failedTypes.failed')
      } else {
        return this.$t('signatureChains.states.pending')
      }
    },
    async showDownloadModal(event) {
      this.downloadMenuVisible = false // Ensure the menu is closed before showing
      this.$nextTick(() => {
        this.downloadMenuVisible = true
      })
      this.downloadMenuX = event.clientX
      this.downloadMenuY = event.clientY

      const answer = await new Promise(resolve => {
        eventBus.$once('downloadModeSelected', data => resolve(data))
      })
      this.downloadModalNewTab = false
      return answer
    },
    async downloadDocument(item, event) {
      // getting the latest document

      const chain = item

      let mode = documentDownloadMode.ORIGINAL
      if (chain.signingTrailMode === documentDownloadMode.DETACHED) {
        mode = await this.showDownloadModal(event)
        if (mode === 'CANCEL') return
      }

      try {
        if (!chain.origDocument && chain.batch) {
          await this.handleBatchDownload(chain, mode)
          return
        }

        const { docId, fileName } = this.getDocIdAndFileName(chain)
        const documentData = await documents.downloadDocument(docId, mode)
        const downloadFileName = this.getDownloadFileName(fileName, mode)

        await FileSaver.saveAs(documentData, downloadFileName)
      } catch (error) {
        showToastError(this.getErrorMessage(error))
      }
    },
    async handleBatchDownload(chain, mode) {
      let docId = chain.batch.id
      const fileName = chain.batch.name

      if (chain.signatures.every(x => x.status === 'CREATED')) {
        try {
          const batch = await documents.getBatch(chain.batch.id)

          batch.documents.forEach(async doc => {
            const documentData = await documents.downloadDocument(doc.id, mode)
            await FileSaver.saveAs(
              documentData.slice(0, documentData.size, 'application/pdf'),
              doc.fileName
            )
          })
          return
        } catch (error) {
          console.error(error)
        }
      } else {
        // ASIC
        const signatures = chain.signatures.filter(signature => signature.signedAt)
        signatures.sort((a, b) => new Date(b.signedAt) - new Date(a.signedAt))
        docId = signatures[0].signedDocument.id

        try {
          const documentData = await documents.downloadDocument(docId, mode)

          await FileSaver.saveAs(documentData, chain.signedDocument.fileName)
        } catch (error) {
          showToastError(this.getErrorMessage(error))
        }
      }
    },
    getDownloadFileName(fileName, mode) {
      const lastDotIndex = fileName.lastIndexOf('.')

      const hasExtension = lastDotIndex !== -1
      const nameWithoutExt = hasExtension ? fileName.substring(0, lastDotIndex) : fileName
      const extension = hasExtension ? fileName.substring(lastDotIndex) : ''

      if (mode === documentDownloadMode.ORIGINAL && extension.toLowerCase() === '.pdf') {
        return fileName
      }
      if (mode === documentDownloadMode.ORIGINAL && extension.toLowerCase() !== '.pdf') {
        return fileName + '.pdf'
      }

      if (mode === documentDownloadMode.FULL) {
        return nameWithoutExt + '.zip'
      }

      return nameWithoutExt + '-signingtrail' + (hasExtension ? extension : '.pdf')
    },
    getDocIdAndFileName(chain) {
      if (chain.signedDocument) {
        return {
          docId: chain.signedDocument.id,
          fileName: chain.signedDocument.fileName.replace(/\.[^/.]+$/, '')
        }
      }

      const lastSignedDoc = chain.signatures.findLast(x => x.signedDocument)
      return {
        docId: lastSignedDoc?.signedDocument?.id || chain.origDocument.id,
        fileName: chain.origDocument?.fileName || 'Document'
      }
    },

    async downloadBatch(chain, mode) {
      const batch = await documents.getBatch(chain.batch.id)
      for (const doc of batch.documents) {
        const documentData = await documents.downloadDocument(doc.id, mode)
        await FileSaver.saveAs(
          documentData.slice(0, documentData.size, 'application/pdf'),
          doc.fileName
        )
      }
    },

    getFilterChipColor() {
      switch (this.statusFilter) {
        case SignatureChainListStatus.ALL:
          return 'filterbuttonTextColor'
        case SignatureChainListStatus.TODO:
          return 'primary'
        case SignatureChainListStatus.SIGNED:
          return 'success'
        case SignatureChainListStatus.PENDING:
          return 'accent'
        case SignatureChainListStatus.FAILED.FAILED:
          return 'error'
        default:
          return 'lime'
      }
    },

    handleUploadClick() {
      if (this.canUpload) {
        this.showUpload()
      } else {
        if (this.sealStatus.enabled) {
          this.centralKeyNotActiveFileDropWarning = true
        }
      }
    },

    showUpload() {
      this.routerPush({ name: 'UploadDocumentWithChain' })
    },

    navToSign(chain) {
      const signId = this.getUserSignatureId(chain)
      const currentSignatureId = this.getCurrentSignatureId(chain)

      const latestSigned = chain.signatures.findLast(sig => sig.signedDocument)
      const docIdToSign = latestSigned ? latestSigned.signedDocument.id : chain.origDocument.id

      const userSignature = chain.signatures.find(sig => sig.id === signId)
      const isInClerkMode = userSignature?.clerkMode
      const fieldParams = userSignature?.signatureFieldParameters

      const query = (fieldParams?.originX != null && fieldParams?.originY != null && fieldParams?.page != null)
        ? {
          originX: fieldParams.originX,
          originY: fieldParams.originY,
          page: fieldParams.page,
          width: fieldParams.width,
          height: fieldParams.height
        }
        : {}

      const path = isInClerkMode
        ? `/document/${docIdToSign}/clerk/${signId}/${currentSignatureId}`
        : `/document/${chain.id}/${docIdToSign}/customization/signature/${signId}/${currentSignatureId}`

      if (isInClerkMode) {
        this.$store.commit('setCurrentChain', chain)
      } else {
        this.$store.commit('setChainsToSign', [chain])
      }

      this.$router.push({ path, query })
    },
    navToApprove(chain) {
      this.$store.commit('setCurrentChain', chain)
      this.routerPush('/document/' + (chain.origDocument.id) + '/approve-customization/' + this.getUserSignatureIdForApprover(chain) + '/' + this.getCurrentSignatureId(chain))
    },

    toggleThumbnailView() {
      this.isInThumbnailView = !this.isInThumbnailView
      this.setPaginationCookies()
    },

    sortingChanged(sortBy) {
      this.currentSorting.text = sortBy.text
      this.currentSorting.value = sortBy.value
      this.$set(this.signatureChainPagination, 'sortBy', [this.currentSorting.value])
      this.setPaginationCookies()
      this.searchSignatureChainsWatcher()
    },

    toggleSort() {
      this.currentSorting.isDesc = !this.currentSorting.isDesc
      this.$set(this.signatureChainPagination, 'sortDesc', [this.currentSorting.isDesc])
      this.setPaginationCookies()
      this.searchSignatureChainsWatcher()
    },

    async loadMore() {
      let realSortField = this.signatureChainPagination.sortBy[0]
      if (realSortField === 'origDocument.fileName') {
        realSortField = 'fileName'
      }

      const nextPage = ++this.pageThumbnail

      const q = {
        status: this.statusFilter == null ? null : this.statusFilter,
        q: this.filters.signatureChainOrigDocFileName
      }

      const pageRequest = {
        'page-num': nextPage,
        'page-size': this.signatureChainPagination.itemsPerPage ? (this.signatureChainPagination.itemsPerPage > 8 ? this.signatureChainPagination.itemsPerPage : 8) : 10,
        'sort-field': realSortField,
        'sort-order': this.signatureChainPagination.sortDesc[0] ? 'desc' : 'asc'
      }

      try {
        const page = await signatureChains.listMySignatureChains(q, pageRequest)
        this.signatureChainList.push(...page.content)

        if (!this.isExternalListing && !this.$route.fullPath.includes('upload')) {
          this.routerReplace({ name: 'SignatureChains', params: { filter: this.statusFilter } })
        }
      } catch (error) {
        showToastError(this.getErrorMessage(error))
      } finally {
        this.dataTableLoading = false
      }
    },

    setPaginationCookies() {
      this.$cookies.set('doc-pagination', { thumbnailView: this.isInThumbnailView }, -1)
    },

    async openInNewTab(chain, event) {
      let mode = documentDownloadMode.ORIGINAL
      if (chain.signingTrailMode === documentDownloadMode.DETACHED) {
        this.downloadModalNewTab = true
        mode = await this.showDownloadModal(event)

        if (mode === 'CANCEL') {
          return
        }
      }
      const docId = chain.signedDocument?.id ??
        chain.signatures.findLast(x => x.signedDocument)?.signedDocument.id ??
        chain.origDocument.id

      try {
        const blob = await documents.downloadDocument(docId, mode)

        const url = URL.createObjectURL(blob)
        window.open(url, '_blank', 'noopener,noreferrer')
      } catch (error) {
        showToastError(this.getErrorMessage(error))
      }

      this.downloadMenuVisible = false
    },

    isInProgress(signatures) {
      const signatureInProgress = signatures.find(x => !!x.user && x.user.id === this.currentUser.id && x.status === SignatureChainStatus.IN_PROGRESS)
      return !!signatureInProgress
    },

    async onChainCreated(event) {
      if (!this.$route.fullPath.includes('upload')) {
        if (this.statusFilter === SignatureChainListStatus.ALL) {
          await this.searchSignatureChainsWatcher()
        } else {
          if (this.canUserSign(event)) {
            await this.searchOrshowNotification(SignatureChainListStatus.TODO)
          } else {
            await this.searchOrshowNotification(SignatureChainListStatus.PENDING)
          }
        }
      }
    },

    async onChainUpdated(event) {
      if (!this.$route.fullPath.includes('upload')) {
        const isUserNext = event.nextUserId && event.nextUserId === this.currentUser.id
        if (this.statusFilter === SignatureChainListStatus.ALL) {
          await this.searchSignatureChainsWatcher()
        } else {
          switch (event.status) {
            case SignatureChainStatus.DONE:
              await this.searchOrshowNotification(SignatureChainListStatus.SIGNED, event.id)
              break
            case SignatureChainStatus.FAILED:
              await this.searchOrshowNotification(SignatureChainStatus.FAILED, event.id)
              break
            case SignatureChainStatus.CREATED:
              if (isUserNext || this.canUserSign(await signatureChains.getSignatureChain(event.id))) {
                await this.searchOrshowNotification(SignatureChainListStatus.TODO, event.id)
              } else {
                await this.searchOrshowNotification(SignatureChainListStatus.PENDING, event.id)
              }
              break
            default:
              break
          }
        }
      }
    },

    onChainDeleted(event) {
      const chainIdx = this.signatureChainList.findIndex(x => x.id === event.id)
      if ((chainIdx !== undefined) && (chainIdx !== null)) {
        const newList = this.signatureChainList.filter(x => x.id !== event.id)
        this.signatureChainList = newList
        this.totalItems--
      }
      if ((this.chosenSignatureChain) && (this.chosenSignatureChain.id === event.id)) {
        this.showParticipantsDialog = false
      }
    },

    async searchOrshowNotification(status, modifiedChainId = undefined) {
      if (this.statusFilter === status) {
        await this.searchSignatureChainsWatcher()
      } else {
        const filter = this.signatureChainStatusFilters.find(x => x.value === status)
        filter.notifications = true
        if (modifiedChainId) {
          const newLocal = this.signatureChainList.filter(x => x.id !== modifiedChainId)
          this.signatureChainList = newLocal
        }
      }
    },

    hideNotifications() {
      const filter = this.signatureChainStatusFilters.find(x => x.value === this.statusFilter)
      switch (this.statusFilter) {
        case SignatureChainListStatus.ALL:
          this.signatureChainStatusFilters.forEach(x => { x.notifications = false })
          break
        case SignatureChainListStatus.TODO:
          filter.notifications = false
          break
        case SignatureChainListStatus.PENDING:
          filter.notifications = false
          break
        case SignatureChainListStatus.SIGNED:
          filter.notifications = false
          break
        case SignatureChainListStatus.FAILED.FAILED:
          filter.notifications = false
          break
        default:
          break
      }
    },

    isViewOnly(chain) {
      const b = chain.viewers.find(x => x.id === this.currentUser.id)
      return !!b
    },

    declineModalClosed() {
      this.declineReasonVisible = false
      this.$emit('changed')
    },

    async listMyActivatedCredentials() {
      try {
        const resp = await rssp.listMyActivatedCredentials()
        resp.content = resp.content.filter(elem => ((elem.keyPurpose === 'ASYMMETRIC_SIGN') && (elem.certChain.length > 0)))

        this.activatedCredentials = resp.content
      } catch (error) {
        const errorMsg = this.getErrorMessage(error)
        // showToastError(errorMsg)
      }
    },
    async switchBulkSign() {
      if (!this.bulkSignMode) {
        this.statusFilter = SignatureChainListStatus.TODO

        Promise.resolve().then(async () => {
          await this.searchSignatureChains()
          const checkLoading = () => {
            if (!this.dataTableLoading) { this.bulkSignMode = true } else {
              setTimeout(checkLoading, 50)
            }
          }; checkLoading()
        })
      } else {
        this.bulkSignMode = false
        this.selected = []
      }
    },
    multiSign() {
      this.$store.commit('setChainsToSign', this.selected.filter(x => !!x.origDocument))
      this.$router.push({ name: 'MultiSignatureCustomization' })
    },
    asicSign(batch) {
      this.$store.commit('setCurrentChain', batch)
      this.$router.push({ name: 'AsicSignatureCustomization' })
    },
    handleSelectionChanged(newSelection) {
      this.selected = newSelection
    }
  }

}

</script>

<style>
/* These are not scoped because they are needed in ./SignatureChainPaticipants.vue */
.signed {
  background-color: var(--v-statusEnabled-base) !important;
  color: var(--v-statusEnabledTextColor-base) !important;
}

.pending {
  background-color: #d1e8ff !important;
  color: #007afe !important;
}

.todo {
  background-color: #fcedcd !important;
  color: #fdb714 !important;
}

.category-btn {
  border-top-right-radius: 0;
  border-bottom-right-radius: 0;
  padding-right: 17px !important;
  margin-right: 2px;
}

.sortdirection-btn {
  border-top-left-radius: 0 !important;
  border-bottom-left-radius: 0 !important;
  padding: 0 !important;
}

.menu-link {
  font-size: 14px;
}
</style>
